import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Monitor,
  Camera,
  AppWindow,
  ClipboardList,
  Usb,
  AlertTriangle,
  Settings,
  Activity,
  FileBarChart,
  Search,
  BarChart3,
  Globe,
  Printer,
  Network,
  FileText,
  ShieldCheck,
  Sliders,
  ShieldAlert,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const adminMenuItems = [
  { text: 'Workers', icon: Monitor, path: '/' },
  { text: 'Analytics', icon: BarChart3, path: '/analytics' },
  { text: 'Policies', icon: ShieldCheck, path: '/policies' },
  { text: 'Control', icon: Sliders, path: '/control' },
  { text: 'Search', icon: Search, path: '/search' },
  { text: 'Settings', icon: Settings, path: '/settings' },
];

const workerMenuItems = [
  { text: 'Worker Info', icon: LayoutDashboard, path: '/worker/:workerId', isWorkerPath: true },
  { text: 'Screenshots', icon: Camera, path: '/screenshots' },
  { text: 'Applications', icon: AppWindow, path: '/applications' },
  { text: 'Clipboard', icon: ClipboardList, path: '/clipboard' },
  { text: 'USB Activity', icon: Usb, path: '/usb' },
  { text: 'Incidents', icon: AlertTriangle, path: '/incidents' },
  { text: 'Browser History', icon: Globe, path: '/browser' },
  { text: 'Printer Activity', icon: Printer, path: '/printer' },
  { text: 'Network Traffic', icon: Network, path: '/network' },
  { text: 'Logs', icon: FileText, path: '/logs' },
  { text: 'Timeline', icon: Activity, path: '/timeline' },
  { text: 'Reports', icon: FileBarChart, path: '/reports' },
];

function Sidebar({ width = 260 }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isWorkerDetailPage = location.pathname.startsWith('/worker/');
  const workerId = isWorkerDetailPage ? location.pathname.split('/')[2] : null;
  const menuItems = isWorkerDetailPage ? workerMenuItems : adminMenuItems;

  return (
    <aside
      style={{ width }}
      className="flex h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
    >
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sidebar-accent/15 ring-1 ring-sidebar-accent/30">
          <ShieldAlert className="h-5 w-5 text-sidebar-accent" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold tracking-tight">Monitoring Desktop</span>
          <span className="text-[11px] uppercase tracking-wider text-sidebar-foreground/50">
            DLP System · v1.0
          </span>
        </div>
      </div>

      <div className="mx-5 h-px bg-sidebar-border" />

      {/* Section label */}
      <div className="px-5 pt-5 pb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
          {isWorkerDetailPage ? 'Worker Monitoring' : 'Administration'}
        </span>
      </div>

      {/* Menu */}
      <nav className="scrollbar-thin flex-1 overflow-y-auto px-3 pb-4">
        <ul className="flex flex-col gap-1">
          {menuItems.map((item) => {
            let itemPath = item.path;
            if (isWorkerDetailPage && !item.isWorkerPath) {
              itemPath = `/worker/${workerId}${item.path}`;
            } else if (item.isWorkerPath) {
              itemPath = `/worker/${workerId}`;
            }
            const active = location.pathname === itemPath;
            const Icon = item.icon;

            return (
              <li key={item.text}>
                <button
                  type="button"
                  onClick={() => navigate(itemPath)}
                  className={cn(
                    'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    'text-sidebar-foreground/70 hover:bg-white/5 hover:text-sidebar-foreground',
                    active &&
                      'bg-sidebar-accent/15 text-sidebar-foreground hover:bg-sidebar-accent/20'
                  )}
                >
                  {active && (
                    <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-sidebar-accent" />
                  )}
                  <Icon
                    className={cn(
                      'h-[18px] w-[18px] shrink-0 transition-colors',
                      active
                        ? 'text-sidebar-accent'
                        : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground/80'
                    )}
                  />
                  <span className="truncate">{item.text}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-sidebar-border px-5 py-4">
        <p className="text-[11px] text-sidebar-foreground/40">
          © 2024 Monitoring System
        </p>
      </div>
    </aside>
  );
}

export default Sidebar;
